import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import VideoWaterfall from '@/components/feature/VideoWaterfall';
import { usePageMeta } from '@/hooks/usePageMeta';
import { videos } from '@/mocks/videos';
import { categories } from '@/mocks/categories';
import { getCategoryName } from '@/utils/video';

const sortOptions = [
  { key: 'latest', label: '最新发布' },
  { key: 'hot', label: '最热播放' },
  { key: 'likes', label: '最多点赞' },
];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const query = params.get('q') ?? '';

  const [input, setInput] = useState(query);
  const [sort, setSort] = useState('latest');
  const [showSuggest, setShowSuggest] = useState(false);

  useEffect(() => {
    setInput(query);
  }, [query]);

  usePageMeta({
    title: query ? `${query} - 搜索结果 - 抖音短视频` : '搜索 - 抖音短视频',
    description: query
      ? `在抖音搜索“${query}”的相关短视频内容，发现更多精彩。`
      : '在抖音搜索你感兴趣的短视频内容，覆盖音乐、搞笑、生活、美食等多个领域。',
    keywords: query ? `${query},抖音搜索,短视频搜索,抖音短视频` : '抖音搜索,短视频搜索',
  });

  const suggestions = useMemo(() => {
    const kw = input.trim().toLowerCase();
    if (!kw) return [];
    const titleHits = videos
      .filter((v) => v.title.toLowerCase().includes(kw))
      .map((v) => v.title)
      .slice(0, 5);
    const catHits = categories
      .filter((c) => c.name.includes(kw))
      .map((c) => `${c.name}（分类）`);
    const tagHits = Array.from(
      new Set(videos.flatMap((v) => v.tags).filter((t) => t.toLowerCase().includes(kw))),
    )
      .slice(0, 3)
      .map((t) => `${t}（标签）`);
    return [...catHits, ...tagHits, ...titleHits].slice(0, 8);
  }, [input]);

  const results = useMemo(() => {
    const kw = query.trim().toLowerCase();
    if (!kw) return [];
    let list = videos.filter((v) => {
      return (
        v.title.toLowerCase().includes(kw) ||
        v.author.toLowerCase().includes(kw) ||
        v.description.toLowerCase().includes(kw) ||
        getCategoryName(v.category).includes(kw) ||
        v.tags.some((t) => t.toLowerCase().includes(kw))
      );
    });
    if (sort === 'hot') list = [...list].sort((a, b) => b.plays - a.plays);
    else if (sort === 'likes') list = [...list].sort((a, b) => b.likes - a.likes);
    else list = [...list].sort((a, b) => (a.publishDate < b.publishDate ? 1 : -1));
    return list;
  }, [query, sort]);

  const doSearch = (kw: string) => {
    const v = kw.trim();
    setShowSuggest(false);
    setParams(v ? { q: v } : {});
  };

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Header />
      <main className="flex-1 pt-16">
        {/* 搜索区 */}
        <section className="w-full px-4 md:px-8 py-8 md:py-10 bg-background-100/60 border-b border-background-200/70">
          <div className="max-w-3xl mx-auto relative">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                doSearch(input);
              }}
              className="flex items-center gap-2"
            >
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400"></i>
                <input
                  value={input}
                  onChange={(e) => {
                    setInput(e.target.value);
                    setShowSuggest(true);
                  }}
                  onFocus={() => setShowSuggest(true)}
                  onBlur={() => window.setTimeout(() => setShowSuggest(false), 150)}
                  placeholder="搜索视频、作者或标签"
                  className="w-full h-12 pl-11 pr-4 rounded-full text-sm bg-background-50 border border-background-200/70 text-foreground-900 placeholder:text-foreground-400 outline-none focus:ring-2 focus:ring-primary-300"
                />
              </div>
              <button
                type="submit"
                className="h-12 px-6 md:px-8 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                搜索
              </button>
            </form>

            {showSuggest && suggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-background-50 border border-background-200/70 rounded-lg py-2 z-30 max-h-80 overflow-y-auto">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onMouseDown={() => {
                      setInput(s.replace(/（.*）/, ''));
                      doSearch(s.replace(/（.*）/, ''));
                    }}
                    className="w-full text-left px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer flex items-center gap-2"
                  >
                    <i className="ri-search-line text-foreground-400 text-xs"></i>
                    <span className="truncate">{s}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {query && (
            <div className="max-w-3xl mx-auto mt-5 flex items-center flex-wrap gap-2 text-sm">
              <span className="text-foreground-500">搜索：</span>
              <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 font-medium">{query}</span>
              <Link to="/search" className="text-xs text-foreground-400 hover:text-primary-600 cursor-pointer ml-1">
                清空
              </Link>
            </div>
          )}
        </section>

        {/* 结果 */}
        <section className="w-full px-4 md:px-8 py-8">
          {!query ? (
            <div className="py-16 flex flex-col items-center text-center">
              <span className="w-16 h-16 rounded-full bg-secondary-100 text-secondary-400 flex items-center justify-center mb-4">
                <i className="ri-search-line text-3xl"></i>
              </span>
              <h1 className="text-lg font-bold text-foreground-950 mb-2">开始你的探索</h1>
              <p className="text-sm text-foreground-500 mb-6">输入关键词，发现更多精彩短视频</p>
              <div className="flex flex-wrap justify-center gap-2 max-w-lg">
                {categories.map((c) => (
                  <Link
                    key={c.key}
                    to={`/category/${c.key}`}
                    className="px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-900 text-xs font-medium cursor-pointer hover:bg-secondary-200 transition-colors flex items-center gap-1"
                  >
                    <i className={`${c.icon} text-sm`}></i>
                    {c.name}
                  </Link>
                ))}
              </div>
            </div>
          ) : results.length === 0 ? (
            <div className="py-16 flex flex-col items-center text-center">
              <span className="w-16 h-16 rounded-full bg-secondary-100 text-secondary-400 flex items-center justify-center mb-4">
                <i className="ri-emotion-sad-line text-3xl"></i>
              </span>
              <h1 className="text-lg font-bold text-foreground-950 mb-2">没有找到相关内容</h1>
              <p className="text-sm text-foreground-500 mb-6">
                换个关键词试试，或者看看下面的热门分类
              </p>
              <button
                type="button"
                onClick={() => {
                  setInput('');
                  setParams({});
                }}
                className="inline-flex items-center gap-2 h-10 px-6 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold cursor-pointer mb-8 whitespace-nowrap"
              >
                <i className="ri-refresh-line"></i>
                清空关键词
              </button>
              <div className="flex flex-wrap justify-center gap-2 max-w-lg">
                {categories.slice(0, 6).map((c) => (
                  <Link
                    key={c.key}
                    to={`/category/${c.key}`}
                    className="px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-900 text-xs font-medium cursor-pointer hover:bg-secondary-200 transition-colors flex items-center gap-1"
                  >
                    <i className={`${c.icon} text-sm`}></i>
                    {c.name}
                  </Link>
                ))}
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
                <p className="text-sm text-foreground-600">
                  找到 <span className="text-primary-600 font-semibold">{results.length}</span> 个相关视频
                </p>
                <div className="flex items-center gap-1 p-1 rounded-full bg-secondary-100">
                  {sortOptions.map((o) => (
                    <button
                      key={o.key}
                      type="button"
                      onClick={() => setSort(o.key)}
                      className={`px-3.5 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                        sort === o.key ? 'bg-background-50 text-primary-600' : 'text-secondary-800'
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
              <VideoWaterfall videos={results} />
            </>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}